import { useCallback, useEffect, useRef, useState } from 'react';
import VirtualizedGridContainerConsts from './VirtualizedGridContainer.settings';
import type { GridBreakpoint } from './VirtualizedGridContainer.types';
import { useVirtualizer } from '@tanstack/react-virtual';
import { colsForWidth } from './VirtualizedGridContainer.utils';

const { DEFAULT_BREAKPOINTS, DEFAULT_ESTIMATED_HEIGHT, DEFAULT_GAP, DEFAULT_OVERSCAN } = VirtualizedGridContainerConsts;

interface UseVirtualizedGridContainerOptions {
	count?: number;
	gridBreakpoints?: GridBreakpoint[];
	estimatedItemHeight?: number;
	gap?: number;
	overscan?: number;
	enableColumnTransition?: boolean;
}

export function useVirtualizedGridContainer({
	count = 0,
	gridBreakpoints = DEFAULT_BREAKPOINTS,
	estimatedItemHeight = DEFAULT_ESTIMATED_HEIGHT,
	gap = DEFAULT_GAP,
	overscan = DEFAULT_OVERSCAN,
	enableColumnTransition = false,
}: UseVirtualizedGridContainerOptions) {
	const parentRef = useRef<HTMLDivElement | null>(null);
	const [columnCount, setColumnCount] = useState(1);
	const [isResizing, setIsResizing] = useState(false);

	const frameId = useRef<number | undefined>(undefined);
	const lastCols = useRef(columnCount);

	const updateColumnCount = useCallback(() => {
		const parent = parentRef.current;
		if (!parent) return;

		const width = parent.clientWidth;
		const nextCols = colsForWidth(width, gridBreakpoints);

		if (lastCols.current !== nextCols) {
			if (enableColumnTransition) {
				setIsResizing(true);
			}

			lastCols.current = nextCols;
			setColumnCount(nextCols);
		}
	}, [gridBreakpoints, enableColumnTransition]);

	const handleTransitionEnd = useCallback(() => {
		if (isResizing) {
			setIsResizing(false);
		}
	}, [isResizing]);

	const scheduleUpdate = useCallback(() => {
		if (frameId.current) cancelAnimationFrame(frameId.current);

		frameId.current = requestAnimationFrame(() => {
			updateColumnCount();
			frameId.current = undefined;
		});
	}, [updateColumnCount]);

	useEffect(() => {
		const parent = parentRef.current;
		if (!parent) return;

		const ro = new ResizeObserver(scheduleUpdate);
		ro.observe(parent);

		let cleanupListener: (() => void) | undefined;
		if (enableColumnTransition) {
			parent.addEventListener('transitionend', handleTransitionEnd);

			cleanupListener = () => {
				parent.removeEventListener('transitionend', handleTransitionEnd);
			};
		}

		return () => {
			ro.disconnect();
			if (frameId.current) cancelAnimationFrame(frameId.current);

			if (cleanupListener) {
				cleanupListener();
			}
		};
	}, [scheduleUpdate, enableColumnTransition, handleTransitionEnd]);

	const virtualizer = useVirtualizer({
		count,
		lanes: columnCount,
		overscan,
		getScrollElement: () => parentRef.current,
		estimateSize: () => estimatedItemHeight + gap,
	});

	const measureFrameId = useRef<number | undefined>(undefined);

	useEffect(() => {
		if (!columnCount) return;

		if (measureFrameId.current) cancelAnimationFrame(measureFrameId.current);

		measureFrameId.current = requestAnimationFrame(() => {
			try {
				virtualizer.measure();
			} catch (error) {
				const parent = parentRef.current;
				console.error('[VirtualizedGridContainer] failed to measure virtualizer', {
					error,
					columnCount,
					parentWidth: parent?.clientWidth ?? null,
					parentHeight: parent?.clientHeight ?? null,
				});
			}

			measureFrameId.current = undefined;
		});

		return () => {
			if (measureFrameId.current) {
				cancelAnimationFrame(measureFrameId.current);
				measureFrameId.current = undefined;
			}
		};
	}, [columnCount, virtualizer]);

	return { parentRef, virtualizer, columnCount, isResizing };
}
